// ═══════════════════════════════════════════════════════════════════
// TMS GAMMA — Transportadoras: registro e lookup por código
//
// Este arquivo é a ÚNICA fonte da lista de transportadoras no cliente.
//
// Antes existia `constants/transportadoras.js`, com as 52 transportadoras
// reais escritas à mão — razão social, código interno e filial. Ele era
// importado estaticamente por 15 módulos, o que colocava a malha logística
// da empresa no bundle que o navegador baixa antes de qualquer login (#32).
//
// A lista real sempre esteve no Supabase; a constante era uma segunda fonte
// do mesmo dado, mantida como fallback — o padrão que o AGENTS.md §6 proíbe.
// Agora o App registra aqui o que veio do banco, e quem precisa consulta.
//
// POR QUE UM REGISTRO E NÃO UMA PROP
//
// Passar a lista por prop seria mais limpo, mas exigiria mudar a assinatura
// de 8 componentes e de helpers que vivem fora deles (Municipios tem três).
// Sem conseguir autenticar para conferir tela por tela, um refactor desse
// tamanho quebraria dropdown em silêncio — que é o modo de falha que esta
// base já conhece.
//
// O registro mantém a substituição mecânica: `TRANSPORTADORAS` virou
// `listarTransportadoras()`, mesma forma, mesmo lugar. Migrar para props
// continua possível depois, um módulo por vez.
// ═══════════════════════════════════════════════════════════════════

/** Lista vigente. Vazia até o App registrar o que veio do Supabase. */
let LISTA = [];

/**
 * Registra a lista vinda do Supabase. Chamado pelo App a cada mudança.
 *
 * Normaliza os dois formatos que circulam na base: o do Supabase
 * (`codigo`/`nome_completo`/`nome_curto`) e o antigo (`cod`/`nome`/`short`).
 * Sem isso, cada consumidor precisaria conhecer os dois.
 */
export function definirTransportadoras(lista) {
  LISTA = (Array.isArray(lista) ? lista : []).map((t) => {
    const cod = t.codigo || t.cod || "";
    return {
      ...t,
      cod,
      nome:  t.nome_completo || t.nome || t.nome_curto || t.short || cod,
      short: t.nome_curto || t.short || t.nome_completo || t.nome || cod,
    };
  });
}

/**
 * A lista vigente.
 *
 * Devolve `[]` antes do registro — nunca `undefined`. Quem itera para montar
 * dropdown recebe lista vazia em vez de quebrar, e quem busca não acha, o que
 * faz o chamador cair no código. Degradação, não erro.
 */
export function listarTransportadoras() {
  return LISTA;
}

export function normCodTransp(s) {
  return String(s || "").replace(/^0+/, "").trim();
}

/**
 * Forma canônica do código, para AGRUPAR.
 *
 * normCodTransp devolve "3", bom para comparar. Para agrupar e mostrar na
 * tela, "000003" é melhor: é a forma do cadastro, e o número exibido
 * continua reconhecível.
 *
 * Existe porque o OTIF estava partido em duas linhas para a mesma
 * transportadora — "000003" com 2.860 entregas no prazo e "3" com 15 —, e as
 * duas linhas estavam erradas, cada uma ignorando as notas da outra (#112).
 * Código com mais de 6 dígitos fica como está.
 */
export function canonCodTransp(s) {
  const n = normCodTransp(s);
  if (!n) return "";
  return n.length >= 6 ? n : n.padStart(6, "0");
}

/**
 * Cria uma função de busca (cod) => { cod, nome, short } | null.
 *
 * Aceita uma lista explícita para quem já a tem em mãos (a maioria dos
 * módulos recebe `transportadoras` por prop); sem argumento, usa o registro.
 *
 * A busca ignora zeros à esquerda: a base mistura "92" e "000092".
 */
export function criarTranspLookup(transportadoras) {
  const fonte = transportadoras?.length ? transportadoras : LISTA;
  const m = new Map();
  fonte.forEach((t) => {
    const cod = t.codigo || t.cod;
    if (!cod) return;
    m.set(normCodTransp(cod), {
      cod,
      nome:  t.nome_completo || t.nome || t.nome_curto || t.short || cod,
      short: t.nome_curto || t.short || t.nome_completo || t.nome || cod,
    });
  });
  return (cod) => m.get(normCodTransp(cod)) || null;
}
